import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ReactECharts from 'echarts-for-react';
import React, { FC, useMemo } from 'react';
import {
  axisLabelStyle,
  formatRupeesIndianCompact,
  premiumTooltip,
  standardGrid,
  defaultAnimation,
} from './echartsConfig';
import { reportChartCanvasSx, reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader from './ReportCardHeader';
import { resolvedOrganizationLabel } from './ConsolidatedRevenueByOrgTable';

const ORG_PALETTE = [
  '#5B8FF9',
  '#61DDAA',
  '#65789B',
  '#F6BD16',
  '#7262FD',
  '#78D3F8',
  '#9661BC',
  '#F6903D',
  '#008685',
  '#F08BB4',
  '#70A1FF',
  '#95DE64',
];

const OTHER_COLOR = '#9CA3AF';

export type OrgContributionSeries = {
  organizationId: number | null;
  organizationName: string;
  contributionPercent: number[];
  confirmedRevenue: number[];
};

export type ContributionTrendsData = {
  monthLabels: string[];
  monthKeys: string[];
  portfolioTotalRevenue: number[];
  series: OrgContributionSeries[];
};

/** Coerce API / JSON values to finite numbers for chart + table. */
function coerceNumArray(arr: unknown): number[] {
  if (!Array.isArray(arr)) {
    return [];
  }
  return arr.map(v => {
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  });
}

/**
 * Normalize payload (snake_case gateways, loose JSON typing) and align array lengths to monthLabels.
 */
function normalizeContributionTrendsData(raw: unknown): ContributionTrendsData | null {
  if (raw == null || typeof raw !== 'object') {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const monthLabels = (r.monthLabels ?? r.month_labels) as string[] | undefined;
  const labels = Array.isArray(monthLabels) ? monthLabels : [];
  const n = labels.length;
  const monthKeys = (r.monthKeys ?? r.month_keys) as string[] | undefined;
  const portfolioRaw = r.portfolioTotalRevenue ?? r.portfolio_total_revenue;
  let portfolioTotalRevenue = coerceNumArray(portfolioRaw);
  if (n > 0 && portfolioTotalRevenue.length < n) {
    portfolioTotalRevenue = [...portfolioTotalRevenue, ...Array(n - portfolioTotalRevenue.length).fill(0)];
  }

  const seriesRaw = r.series;
  const series: OrgContributionSeries[] = [];
  if (Array.isArray(seriesRaw)) {
    for (const item of seriesRaw) {
      if (item == null || typeof item !== 'object') {
        continue;
      }
      const s = item as Record<string, unknown>;
      const oid = s.organizationId ?? s.organization_id;
      let pcts = coerceNumArray(s.contributionPercent ?? s.contribution_percent);
      let revs = coerceNumArray(s.confirmedRevenue ?? s.confirmed_revenue);
      if (n > 0) {
        if (pcts.length < n) {
          pcts = [...pcts, ...Array(n - pcts.length).fill(0)];
        }
        if (revs.length < n) {
          revs = [...revs, ...Array(n - revs.length).fill(0)];
        }
      }
      series.push({
        organizationId: oid == null ? null : Number(oid),
        organizationName: String(s.organizationName ?? s.organization_name ?? ''),
        contributionPercent: pcts,
        confirmedRevenue: revs,
      });
    }
  }

  return {
    monthLabels: labels,
    monthKeys: Array.isArray(monthKeys) ? monthKeys : [],
    portfolioTotalRevenue,
    series,
  };
}

/** Latest month index in the range with any portfolio confirmed revenue (avoids trailing empty months → 0% for all). */
function lastIndexWithPositivePortfolio(portfolioTotals: number[], upTo: number): number {
  for (let i = upTo; i >= 0; i--) {
    if (Number(portfolioTotals[i]) > 0) {
      return i;
    }
  }
  return -1;
}

function contributionPctAt(
  s: OrgContributionSeries,
  idx: number,
  portfolioTotal: number,
): number {
  if (idx < 0) {
    return 0;
  }
  const pcts = s.contributionPercent || [];
  const revs = s.confirmedRevenue || [];
  const fromSeries = idx < pcts.length ? Number(pcts[idx]) : NaN;
  if (Number.isFinite(fromSeries)) {
    return fromSeries;
  }
  const rev = idx < revs.length ? Number(revs[idx]) || 0 : 0;
  if (portfolioTotal > 0) {
    return (rev / portfolioTotal) * 100;
  }
  return 0;
}

/** Shown on the standard info icon — one compact line. */
const INSIGHT_SHORT =
  'Contribution % = (organization confirmed revenue ÷ portfolio total for that month) × 100. CONFIRMED only, by event start month.';

/** Shown on the (?) help icon — full context. */
const HELP_DETAIL = `Why this chart
Total portfolio revenue can look flat while the mix between organizations shifts. This view shows who drives revenue each month and how that share changes.

Formula
Contribution % = (organization confirmed revenue for that calendar month ÷ total portfolio confirmed revenue for that month) × 100.

Data rules
Uses booking line items with status CONFIRMED, grouped by event start month. Tooltips include both % and currency.

Series cap
When there are more than 12 organizations, the remainder are combined as "Other organizations".

Visualization
Stacked area: each colored layer is one organization; height is that month’s share of confirmed revenue (sums to 100% across layers).`;

const TOP_CONTRIBUTORS_LIMIT = 8;

function trendArrows(delta: number): string {
  if (delta >= 2.5) {
    return '↑↑';
  }
  if (delta >= 0.4) {
    return '↑';
  }
  if (delta <= -2.5) {
    return '↓↓';
  }
  if (delta <= -0.4) {
    return '↓';
  }
  return '→';
}

type TopContributorRow = {
  orgId: number | null;
  name: string;
  contributionPct: number;
  trend: string;
};

function buildTopContributors(data: ContributionTrendsData): TopContributorRow[] {
  const series = data.series || [];
  const n = data.monthLabels?.length ?? 0;
  if (!series.length || n < 1) {
    return [];
  }
  const portfolioTotals = data.portfolioTotalRevenue || [];
  const calendarLast = n - 1;
  let last = lastIndexWithPositivePortfolio(portfolioTotals, calendarLast);
  if (last < 0) {
    last = calendarLast;
  }
  const totalLast = Number(portfolioTotals[last]) || 0;
  let prev = lastIndexWithPositivePortfolio(portfolioTotals, last - 1);
  if (prev < 0 || prev === last) {
    prev = last > 0 ? last - 1 : -1;
  }
  const totalPrev = prev >= 0 ? Number(portfolioTotals[prev]) || 0 : 0;
  const canTrend = prev >= 0 && totalLast > 0 && totalPrev > 0;

  const rows: TopContributorRow[] = series.map(s => {
    const pctLast = contributionPctAt(s, last, totalLast);
    const pctPrev = prev >= 0 ? contributionPctAt(s, prev, totalPrev) : pctLast;
    const delta = canTrend ? pctLast - pctPrev : 0;
    const trend = canTrend ? trendArrows(delta) : '—';
    const name = resolvedOrganizationLabel(s.organizationId, s.organizationName);
    return {
      orgId: s.organizationId ?? null,
      name,
      contributionPct: pctLast,
      trend,
    };
  });

  return rows
    .sort((a, b) => b.contributionPct - a.contributionPct)
    .slice(0, TOP_CONTRIBUTORS_LIMIT);
}

interface ContributionTrendsChartProps {
  data: ContributionTrendsData | null;
  loading?: boolean;
  emptyMessage?: string;
}

const ContributionTrendsChart: FC<ContributionTrendsChartProps> = ({
  data,
  loading = false,
  emptyMessage = 'No data for this range.',
}) => {
  const effectiveData = useMemo(() => normalizeContributionTrendsData(data), [data]);
  const topContributors = useMemo(
    () => (effectiveData ? buildTopContributors(effectiveData) : []),
    [effectiveData],
  );

  const option = useMemo(() => {
    if (!effectiveData?.monthLabels?.length || !effectiveData?.series?.length) {
      return null;
    }
    const monthLabels = effectiveData.monthLabels;
    const portfolioTotals = effectiveData.portfolioTotalRevenue || [];
    const seriesDto = effectiveData.series;
    const colors = seriesDto.map((s, i) => {
      const disp = resolvedOrganizationLabel(s.organizationId, s.organizationName);
      return disp.toLowerCase().includes('other') ? OTHER_COLOR : ORG_PALETTE[i % ORG_PALETTE.length];
    });

    return {
      ...defaultAnimation,
      color: colors,
      grid: {
        ...standardGrid,
        top: 28,
        bottom: seriesDto.length > 6 ? 76 : 52,
        right: 12,
      },
      legend: {
        type: 'scroll',
        bottom: 0,
        textStyle: { ...axisLabelStyle, fontSize: 10 },
      },
      tooltip: {
        ...premiumTooltip,
        formatter: (params: unknown) => {
          const list = Array.isArray(params) ? params : params ? [params] : [];
          if (!list.length) {
            return '';
          }
          const first = list[0] as { dataIndex?: number; axisValueLabel?: string };
          const idx = Number(first.dataIndex);
          const month = String(first.axisValueLabel ?? '');
          const total = Number(portfolioTotals[idx]) || 0;
          let html =
            `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${month}</div>` +
            `<div style="margin-bottom:8px;color:#94a3b8;font-size:11px">Portfolio (confirmed): <span style="color:#e2e8f0;font-weight:600">${formatRupeesIndianCompact(total)}</span></div>`;
          const sorted = [...list].sort(
            (a: any, b: any) => (Number(b.value) || 0) - (Number(a.value) || 0),
          );
          for (const p of sorted as any[]) {
            const si = Number(p.seriesIndex);
            const rev = Number(seriesDto[si]?.confirmedRevenue?.[idx]) || 0;
            const pct = Number(p.value) || 0;
            if (pct === 0 && rev === 0) {
              continue;
            }
            const marker = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px;flex-shrink:0"></span>`;
            html +=
              `<div style="display:flex;align-items:center;gap:8px;margin-top:3px;flex-wrap:wrap">` +
              `${marker}` +
              `<span style="flex:1;min-width:0;color:#e2e8f0">${p.seriesName}</span>` +
              `<span style="font-weight:600;font-variant-numeric:tabular-nums">${pct.toFixed(1)}%</span>` +
              `<span style="font-weight:500;color:#cbd5e1;font-variant-numeric:tabular-nums">${formatRupeesIndianCompact(rev)}</span>` +
              `</div>`;
          }
          return html;
        },
      },
      xAxis: {
        type: 'category',
        data: monthLabels,
        axisLabel: {
          ...axisLabelStyle,
          rotate: monthLabels.length > 14 ? 32 : 0,
        },
        axisLine: { lineStyle: { color: '#E5E7EB' } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: 100,
        name: 'Contribution %',
        nameTextStyle: { ...axisLabelStyle, fontSize: 11, fontWeight: 600 },
        axisLabel: {
          ...axisLabelStyle,
          formatter: (v: number) => `${v}%`,
        },
        splitLine: { lineStyle: { color: '#EEF2F7', type: 'dashed' } },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: seriesDto.map(s => ({
        name: resolvedOrganizationLabel(s.organizationId, s.organizationName),
        type: 'line',
        stack: 'contribution',
        areaStyle: { opacity: 0.58 },
        emphasis: { focus: 'series' },
        lineStyle: { width: 1 },
        smooth: 0.25,
        symbol: 'circle',
        symbolSize: 3,
        data: (s.contributionPercent || []).map(v => (typeof v === 'number' ? v : Number(v) || 0)),
      })),
    };
  }, [effectiveData]);

  const helpTooltip = (
    <Box component="span" sx={{ display: 'block', maxWidth: 380, whiteSpace: 'pre-line', fontSize: 11, lineHeight: 1.55 }}>
      {HELP_DETAIL}
    </Box>
  );

  const headerTrailing = (
    <Tooltip
      title={helpTooltip}
      arrow
      placement="left-start"
      enterTouchDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(15, 23, 42, 0.96)',
            color: '#f1f5f9',
            border: '1px solid rgba(148, 163, 184, 0.25)',
            maxWidth: 420,
            px: 1.5,
            py: 1,
          },
        },
        arrow: { sx: { color: 'rgba(15, 23, 42, 0.96)' } },
      }}>
      <IconButton
        size="small"
        aria-label="More about contribution trends"
        sx={{
          p: 0.15,
          minWidth: 28,
          ml: 0.25,
          color: '#94a3b8',
          fontSize: 12,
          fontWeight: 700,
          borderRadius: 1,
          '&:hover': { color: '#475569', bgcolor: 'rgba(148, 163, 184, 0.12)' },
        }}>
        <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1, letterSpacing: -0.5 }}>
          (?)
        </Typography>
      </IconButton>
    </Tooltip>
  );

  const oneLiner =
    'Stacked share of confirmed revenue by organization each month. Use the chart for mix over time; the table ranks the latest month.';

  return (
    <Paper elevation={0} sx={reportChartPaperSx}>
      <ReportCardHeader
        title="Contribution trends"
        insight={INSIGHT_SHORT}
        showButton={false}
        trailing={headerTrailing}
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25, lineHeight: 1.45, px: 0.25 }}>
        {oneLiner}
      </Typography>

      {loading ? (
        <Skeleton variant="rounded" height={420} sx={{ borderRadius: 1 }} />
      ) : !option ? (
        <Typography variant="body2" color="text.secondary">
          {emptyMessage}
        </Typography>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' },
            gap: 2,
            alignItems: 'stretch',
          }}>
          <Box sx={{ minWidth: 0, minHeight: 400 }}>
            <Box sx={{ ...reportChartCanvasSx, minHeight: 400, height: 400 }}>
              <ReactECharts option={option} style={{ width: '100%', height: '100%' }} notMerge lazyUpdate />
            </Box>
          </Box>
          <Box
            sx={{
              minWidth: 0,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#fafbfc',
              p: 1.25,
              display: 'flex',
              flexDirection: 'column',
            }}>
            <Typography variant="subtitle2" fontWeight={700} color="text.primary" sx={{ mb: 1 }}>
              Top contributors
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1, lineHeight: 1.4 }}>
              Latest month share vs prior month (↑ / ↑↑ / → / ↓ / ↓↓).
            </Typography>
            <Table
              size="small"
              sx={{
                tableLayout: 'fixed',
                width: '100%',
                '& .MuiTableCell-root': { py: 0.5, px: 0.75, fontSize: 12 },
              }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'rgba(148, 163, 184, 0.12)' }}>
                  <TableCell sx={{ fontWeight: 700, width: '46%' }}>Organization</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '32%' }} align="right">
                    Contribution
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, width: '22%' }} align="center">
                    Trend
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {topContributors.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        No contributor breakdown for the latest month in this range.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  topContributors.map(row => (
                    <TableRow key={row.orgId != null ? `org-${row.orgId}` : row.name} hover>
                      <TableCell sx={{ maxWidth: 0, overflow: 'hidden', verticalAlign: 'middle' }}>
                        <Tooltip title={row.name} placement="top-start" arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              width: '100%',
                            }}>
                            {row.name}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
                        {row.contributionPct.toFixed(1)}%
                      </TableCell>
                      <TableCell align="center" sx={{ fontSize: 15, letterSpacing: 0.5 }}>
                        {row.trend}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

export default ContributionTrendsChart;
